/**
 * Virtual Core Session: creates sessions in virtual-core database.
 * Migrated from caribe7/services/virtual-core/session.js.
 */

import RequestClient from 'shared/api/http-client';

/** User data from platform validation, passed when creating a session. */
export interface VirtualCoreUserData {
  user_id?: string | number;
  skin_id?: string | number;
  language?: string;
  timezone?: string;
  odds_format?: string;
  currency?: string;
  balance?: number;
}

/** Response from virtual-core session register endpoint. */
export interface VirtualCoreSessionResponse {
  session_id: string;
  [key: string]: unknown;
}

/**
 * Create virtual-core session.
 *
 * @param platformSessionId - Platform session ID (for reference)
 * @param userData - User data from platform validation
 * @returns Promise resolving to session object with session_id
 */
export async function createVirtualCoreSession(
  platformSessionId: string,
  userData: VirtualCoreUserData
): Promise<VirtualCoreSessionResponse> {
  const apiUrl =
    process.env.REACT_APP_API_URL || 'http://virtual-sports-api.caribe7.local';
  const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
  const endpoint = `${baseUrl}/web/sessions/register`;

  let oddsFormat = 'decimal';
  if (userData.odds_format) {
    const format = userData.odds_format.toLowerCase();
    if (format === 'eu') {
      oddsFormat = 'decimal';
    } else if (format === 'uk') {
      oddsFormat = 'fractional';
    } else if (format === 'us') {
      oddsFormat = 'moneyline';
    }
  }

  const hasDecimals = userData.odds_format?.toLowerCase() === 'eu' ? 1 : 0;

  const playerId = userData.user_id != null ? String(userData.user_id).trim() : '';
  const skinId = userData.skin_id != null ? String(userData.skin_id).trim() : '';
  if (!playerId) throw new Error('Missing player_id parameter');
  if (!skinId) throw new Error('Missing skin_id parameter');

  const payload = {
    platform_session_id: platformSessionId,
    player_id: userData.user_id,
    skin_id: userData.skin_id,
    language: userData.language || 'EN',
    timezone: userData.timezone || 'UTC',
    odds_format: oddsFormat,
    currency: userData.currency || 'USD',
    has_decimals: hasDecimals,
    player_balance: userData.balance ?? 0,
  };

  try {
    const response = await RequestClient.post<VirtualCoreSessionResponse>(endpoint, payload);
    return response.data;
  } catch (error) {
    console.error('[VirtualCoreSession] Error creating session:', error);
    throw error;
  }
}

export default {
  createVirtualCoreSession,
};
